import MojitoServices from 'mojito/services';
import MojitoCore from 'mojito/core';
import PrebuiltCardTypes from 'modules/prebuilt-bets/types/index.js';

const {
    actions: betslipActions,
    utils: BetslipUtils,
    selectors: { selectSelectionState, selectBetslip },
} = MojitoServices.Betslip;
const { dispatch } = MojitoCore.Services.redux.store;
const { arraysAreEqual } = MojitoCore.Base.objUtils;

const ACTIVE_STATE = { isPending: false };
const { PREBUILT_BET_BUILDER_FIXED } = PrebuiltCardTypes.MARKETS;

/**
 * Context object defines actions to add and remove prebuilt bet to the betslip
 * and selector function to get current state of added selections from the betslip store.
 *
 * @class PrebuiltBetSelectionsContext
 * @type {Mojito.Modules.SelectionButton.Context.SelectionsContextDefinition}
 * @memberof Mojito.Modules
 */
export default class PrebuiltBetSelectionsContext {
    constructor(market, boostedMarket, event) {
        this.market = market;
        this.boostedMarket = boostedMarket;
        this.event = event;
    }

    addSelection(payload) {
        dispatch(betslipActions.addSelection(payload));
    }

    removeSelection(payload) {
        const { selectionId, source, bettingContext } = payload;
        const { name: marketName, selections, type: selectionType } = this.market;
        const eventName = this.event?.canonicalName;
        const selectionLabel = selections?.[0].name;
        const selectionPrice = selections?.[0]?.prices?.[0].decimalLabel;
        dispatch(
            betslipActions.removePreBuiltSelection({
                marketName,
                eventName,
                selectionLabel,
                selectionType,
                selectionPrice,
                selectionId,
                source,
                bettingContext,
            })
        );
    }
    selectionStateSelector(state, selectionId) {
        const mathAccaSelectionState = selectSelectionState(selectionId, state);
        if (mathAccaSelectionState?.isPending) {
            return mathAccaSelectionState;
        }
        const parts = this.market?.preBuiltBetBuilderParts || [];
        const isFixedMarket = this.market?.type === PREBUILT_BET_BUILDER_FIXED;
        const betslip = selectBetslip(state);
        const betBuilderBets = BetslipUtils.getMatchAccaBets(betslip);
        const relevantBetBuilderBet = betBuilderBets.find(bet => {
            const selectionIds = BetslipUtils.getSelectionIdsFromBet(bet).sort();
            const partIds = parts.map(p => p.id).sort();
            return arraysAreEqual(partIds, selectionIds);
        });

        // For PBMF market (fixed) type when user adds it to the betslip he will not be able to see individual single bets per each selection inside PBMF.
        // But if user adds those selections manually one by one, then in a betslip there will be normal PBBB or PBAC.
        // Hence, for fixed market (PBMF) we will check if selections are only inside relevantBetBuilderBet parts and do not live as individual singles, only in this case it can be highlighted.
        if (isFixedMarket && relevantBetBuilderBet) {
            const partIdsFromSingles = BetslipUtils.getPartsFromSingles(betslip).map(
                part => part.selectionId
            );
            const partsWereAddedIndividually = relevantBetBuilderBet.parts.every(part =>
                partIdsFromSingles.includes(part.selectionId)
            );
            if (partsWereAddedIndividually) {
                return;
            }
        }

        const everyPartInSlip =
            !!relevantBetBuilderBet ||
            (parts.length !== 0 &&
                parts.every(part => selectSelectionState(part.id, state)?.isPending === false));

        const isPriceBoost = Boolean(relevantBetBuilderBet?.baseOdds);
        const boostedPreBuiltBetBuilderMarketIds =
            this.event?.boostedPreBuiltBetBuilderMarketIds || [];
        const isPriceBoostedMarket = boostedPreBuiltBetBuilderMarketIds.includes(
            this.boostedMarket?.id || this.market.id
        );

        if (everyPartInSlip && isPriceBoostedMarket && isPriceBoost) {
            return ACTIVE_STATE;
        }

        if (everyPartInSlip && !isPriceBoostedMarket && !isPriceBoost) {
            return ACTIVE_STATE;
        }
    }
}
